

import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatNumber } from "utils/numberUtils";
import Tooltip from "@mui/material/Tooltip";

function ComplexStatisticsCard({
  color = "info",
  title,
  count,
  percentage = {
    color: "success",
    text: "",
    label: "",
  },
  icon,
  description = ""
}) {
  return (
    <Card
      sx={{
        boxShadow: 2,
        transition: "all 0.3s ease-in-out",
        '&:hover': { boxShadow: 6 },
      }}
    >
      {/* Top section: icon + title */}
      <MDBox display="flex" justifyContent="space-between" px={2} py={1}>
        <Tooltip title={description || title} placement="top" arrow>
          <MDBox
            variant="gradient"
            bgColor={color}
            // color={color === "light" ? "dark" : "white"}
            color="black"
            coloredShadow={color}
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3rem"
            height="3rem"
            mt={-3} // reduced from -3
            sx={{
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
              cursor: "help", // Indicate interactivity
            }}
          >
            <Icon fontSize="medium" color="inherit">
              {icon}
            </Icon>
          </MDBox>
        </Tooltip>

        <MDBox textAlign="right" lineHeight={1.2} sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          <MDTypography
            variant="button"
            fontWeight="light"
            color="text"
            mb={0.5}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block"
            }}
          >
            {title}
          </MDTypography>
          {typeof count === "number" ? (
            <Tooltip title={`${count.toLocaleString()}`} placement="bottom-end" arrow>
              <MDTypography variant="h4" fontWeight="medium">
                {formatNumber(count)}
              </MDTypography>
            </Tooltip>
          ) : (
            <MDTypography variant="h4" fontWeight="medium">
              {count}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      <Divider sx={{ my: 1 }} />

      {/* Bottom section: growth/percentage */}
      <MDBox pb={1} px={2}>
        <MDTypography component="p" variant="button" color="text" display="flex" alignItems="center">
          {percentage.tooltip ? (
            <Tooltip title={percentage.tooltip} placement="bottom" arrow>
              <MDTypography
                component="span"
                variant="button"
                fontWeight="bold"
                color={percentage.color}
                display="flex"
                alignItems="center"
                sx={{ cursor: "pointer" }}
              >
                {percentage.amount}&nbsp;
                {parseFloat(percentage.amount) > 0 ? (
                  <ArrowUpwardIcon sx={{ fontSize: 16, color: "green" }} />
                ) : parseFloat(percentage.amount) < 0 ? (
                  <ArrowDownwardIcon sx={{ fontSize: 16, color: "red" }} />
                ) : null}
              </MDTypography>
            </Tooltip>
          ) : (
            <MDTypography
              component="span"
              variant="button"
              fontWeight="bold"
              color={percentage.color}
              display="flex"
              alignItems="center"
            >
              {percentage.amount}&nbsp;
              {parseFloat(percentage.amount) > 0 ? (
                <ArrowUpwardIcon sx={{ fontSize: 16, color: "green" }} />
              ) : parseFloat(percentage.amount) < 0 ? (
                <ArrowDownwardIcon sx={{ fontSize: 16, color: "red" }} />
              ) : null}
            </MDTypography>
          )}
        </MDTypography>
      </MDBox>
    </Card>

  );
}

// Setting default values for the props of ComplexStatisticsCard
// ComplexStatisticsCard.defaultProps = {
//   color: "info",
//   percentage: {
//     color: "success",
//     text: "",
//     label: "",
//   },
//   description: "",
// };

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "accent",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "accent",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
  description: PropTypes.string,
};

export default ComplexStatisticsCard;

